// AIDEV-NOTE: Manages Web Push subscription — requests VAPID key, registers SW subscription
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import api from '../services/auth.service';

// AIDEV-NOTE: Converte base64 URL-safe para Uint8Array (necessario para applicationServerKey)
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // AIDEV-NOTE: false enquanto verifica, null se VAPID nao configurado no servidor
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const { isAuthenticated } = useAuth();

  // Verifica se VAPID esta disponivel no servidor
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get<{ vapidPublicKey: string }>('/notifications/vapid-public-key')
      .then(() => setIsAvailable(true))
      .catch(() => setIsAvailable(false));
  }, [isAuthenticated]);

  // Verifica se ja existe subscription ativa ao montar
  useEffect(() => {
    if (!isAuthenticated || typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    navigator.serviceWorker.ready.then((registration) => {
      registration.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(!!sub);
      });
    });
  }, [isAuthenticated]);

  const subscribe = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);

    try {
      // AIDEV-NOTE: requestPermission DEVE ser a primeira operacao async — browsers exigem que
      // seja chamada dentro do contexto de gesto do usuario. Qualquer await de rede antes disso
      // quebra esse contexto e o dialogo nao aparece (falha silenciosa).
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;

      // 2. Busca VAPID public key do servidor
      const { data } = await api.get<{ vapidPublicKey: string }>('/notifications/vapid-public-key');

      // 3. Registra subscription no SW
      // AIDEV-NOTE: applicationServerKey deve ser Uint8Array — passar .buffer (ArrayBuffer) causa
      // DOMException em alguns browsers (Chrome/Firefox rejeitam ArrayBuffer direto).
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.vapidPublicKey)
      });

      // 4. Envia subscription para o servidor
      const subJson = subscription.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth
        }
      });

      setIsSubscribed(true);
    } catch (error) {
      console.error('[Push] Erro ao se inscrever:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove do servidor
        await api.delete('/notifications/subscribe', {
          data: { endpoint: subscription.endpoint }
        });

        // Remove do SW
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (error) {
      console.error('[Push] Erro ao cancelar inscricao:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // AIDEV-NOTE: Listener de mensagens do SW (push em foreground)
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION') {
        // AIDEV-TODO: Integrar com sistema de toast/snackbar do MUI
        console.log('[Push] Notificacao em foreground:', event.data.payload);
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  return { permission, isSubscribed, isLoading, isAvailable, subscribe, unsubscribe };
}
